/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import type { LearnerProfile_EducationHistory as EducationHistory } from '__generated__/graphql-types-do-not-use';

import { useRetracked } from 'js/lib/retracked';

import { Button, Link, Typography2, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type EducationEmptyStateProps = {
  openEducationDialog: (education?: EducationHistory) => void;
};

const styles = {
  wrapperDiv: () => css`
    display: flex;
    align-items: center;
    background-color: var(--cds-color-grey-25);
    padding: var(--cds-spacing-200);
    border-radius: 4px;
    gap: var(--cds-spacing-100);

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: start;
    }
  `,
  descText: css`
    flex-grow: 1;
  `,
  actionButton: css`
    flex-shrink: 0;
  `,
};

const E2E_SELECTORS = {
  ADD_EDUCATION: 'profile-education-section-add',
} as const;

export const EducationEmptyState = ({ openEducationDialog }: EducationEmptyStateProps) => {
  const { restrictHiringSolutions } = useContext(ProfileContext);

  const getBodyText = () => {
    return restrictHiringSolutions
      ? _t('Add your educational background here to let viewers know where you studied or are currently studying.')
      : _t('Add your educational background here to let employers know where you studied or are currently studying.');
  };

  return (
    <div css={styles.wrapperDiv} data-testid="credentials-education-empty-state">
      <Typography2 component="p" variant="bodyPrimary" css={styles.descText}>
        {getBodyText()}
      </Typography2>
      <Button
        variant="secondary"
        size="small"
        icon={<AddIcon />}
        iconPosition="before"
        css={styles.actionButton}
        onClick={() => openEducationDialog()}
        component={TrackedButton}
        requireFullyVisible={false}
        data-e2e={E2E_SELECTORS.ADD_EDUCATION}
        withVisibilityTracking={true}
        // not tracked in eventing v3
        trackingName="profile_add_education_history_empty"
      >
        {_t('Add education')}
      </Button>
    </div>
  );
};

export const CredentialEmptyState = () => {
  const { sharedEventingV3Data, restrictHiringSolutions } = useContext(ProfileContext);
  const retrackedTrack = useRetracked();
  const track = useTracker();

  const certHref = '/certificates/launch-your-career';

  const visibilityTrackingRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.educationHistoryBrowseProfessionalCertificates,
      linkType: 'internal',
      linkURL: certHref,
    },
    ...sharedEventingV3Data,
  });

  const getBodyText = () => {
    return restrictHiringSolutions
      ? _t('Level up with role-based training from industry-leading companies like Google, Meta, and IBM.')
      : _t('Get job-ready with role-based training from industry-leading companies like Google, Meta, and IBM.');
  };

  return (
    <div css={styles.wrapperDiv} data-testid="credentials-credential-empty-state">
      <Typography2 component="p" variant="bodyPrimary" css={styles.descText}>
        {getBodyText()}
      </Typography2>
      {/* I wrap the link with a TrackedDiv for retracked visibility tracking since I cannot use tracked components on
      CDS links. It breaks eventing V3's visibility tracking, which relies on an HTML ref. Using component=
      {TrackedLink2} on a CDS link causes the ref to refer to the TrackedLink component itself, *not* the
      underlying HTML element, which in turn results in the IntersectionObserver used in `useVisibilityTracker()` to 
      throw an error since we're not referencing an html element. */}
      <TrackedDiv
        trackingName={EVENTS.educationHistoryBrowseProfessionalCertificates}
        withVisibilityTracking
        requireFullyVisible={false}
        trackClicks={false}
      >
        <Link
          ref={visibilityTrackingRef}
          href={certHref}
          typographyVariant="subtitleMedium"
          css={styles.actionButton}
          target="_blank"
          onClick={() => {
            retrackedTrack({
              trackingName: EVENTS.educationHistoryBrowseProfessionalCertificates,
              trackingData: {},
              action: 'click',
            });

            track('click_button', {
              button: {
                name: EVENTS.educationHistoryBrowseProfessionalCertificates,
                linkType: 'internal',
                linkURL: certHref,
              },
              ...sharedEventingV3Data,
            });
          }}
        >
          {_t('Browse Professional Certificates')}
        </Link>
      </TrackedDiv>
    </div>
  );
};
